import { useEffect, useRef, useState } from 'react';

function toMillis(input?: string | number | Date | null): number | null {
  if (input == null) return null;
  if (input instanceof Date) return input.getTime();
  if (typeof input === 'number') return input;
  const parsed = Date.parse(input);
  return Number.isNaN(parsed) ? null : parsed;
}

export function formatCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.round(remainingMs / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Live `m:ss` countdown to the table hold's expiry, ticking once a second.
 * Returns `null` when there is no hold to show. `onExpire` fires once, when it hits zero —
 * the caller decides what that means (the widget swaps in the expired state card).
 */
export function useHoldCountdown(
  expiresAt?: string | number | Date | null,
  onExpire?: () => void
): string | null {
  const target = toMillis(expiresAt);
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    firedRef.current = false;
  }, [target]);

  useEffect(() => {
    if (target == null) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (target == null) return null;

  const remaining = target - now;
  if (remaining <= 0 && !firedRef.current) {
    firedRef.current = true;
    // Defer so we never call back into a parent's setState during this render.
    setTimeout(() => onExpireRef.current?.(), 0);
  }

  return formatCountdown(remaining);
}

export default useHoldCountdown;
